import { GalleryHorizontalEnd, ImageIcon, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getGallery } from "../lib/api";
import { formatDate } from "../lib/format";
import { publicEventStyle, safeAssetUrl } from "../lib/personalization";
import type { MediaItem } from "../lib/types";
import { useConfig } from "../lib/useConfig";
import { useEventPersonalization } from "../lib/useEventPersonalization";

export function GalleryPage() {
  const params = useParams();
  const config = useConfig();
  const clientSlug = params.clientSlug || config?.clientSlug;
  const eventSlug = params.eventSlug || config?.eventSlug;
  const eventDetails = useEventPersonalization(clientSlug, eventSlug);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  const load = async (next?: string) => {
    if (!clientSlug || !eventSlug) return;
    setLoading(true);
    const result = await getGallery(clientSlug, eventSlug, next);
    setMedia((current) => (next ? [...current, ...result.media] : result.media));
    setCursor(result.nextCursor);
    setLoading(false);
  };

  useEffect(() => {
    if (!clientSlug || !eventSlug) return;
    void load();
  }, [clientSlug, eventSlug]);

  if (!clientSlug || !eventSlug) return null;
  const logo = safeAssetUrl(eventDetails?.optionalLogo);
  const heroImage = safeAssetUrl(eventDetails?.optionalHeroImage);

  return (
    <main className="px-4 py-12 sm:px-6 lg:px-8" style={publicEventStyle(eventDetails)}>
      <section className="mx-auto max-w-7xl">
        <header className="event-masthead overflow-hidden rounded-lg p-8 text-white shadow-luxe sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_.72fr] lg:items-end">
            <div>
              {logo && <img src={logo} alt={`${eventDetails?.clientName || "Client"} logo`} className="mb-6 max-h-14 max-w-44 object-contain" />}
              <p className="text-xs font-extrabold uppercase tracking-[0.24em]" style={{ color: "var(--event-accent)" }}>
                {eventDetails?.eventType || "Event"} Gallery
              </p>
              <h1 className="mt-4 text-balance font-display text-4xl font-bold sm:text-5xl">
                {eventDetails?.galleryTitle || "Memory Gallery"}
              </h1>
              <p className="mt-4 max-w-3xl leading-7 text-white/66">
                {eventDetails?.galleryIntroText ||
                  "Approved photos and videos from this celebration will appear here after review."}
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={`/memory-media-vault/${clientSlug}/${eventSlug}`}
                  className="focus-ring event-button-secondary inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-extrabold"
                >
                  {eventDetails?.memoryHubButtonLabel || "Back to Vault"}
                </Link>
                <Link
                  to={`/upload/${clientSlug}/${eventSlug}`}
                  className="focus-ring event-button inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-extrabold"
                >
                  Upload
                </Link>
              </div>
            </div>
            {heroImage ? (
              <img
                src={heroImage}
                alt={eventDetails?.eventName || "Event"}
                className="aspect-[4/3] w-full rounded-lg border border-white/15 object-cover"
              />
            ) : (
              <div className="event-panel grid aspect-[4/3] place-items-center rounded-lg border border-white/15 bg-white/[0.06]">
                <GalleryHorizontalEnd className="size-14" style={{ color: "var(--event-accent)" }} />
              </div>
            )}
          </div>
        </header>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {media.map((item) => (
            <article key={item.id} className="gallery-card overflow-hidden rounded-lg border border-ktd-gold/20 bg-ktd-ivory text-ktd-rich shadow-luxe">
              <div className="aspect-[4/3] bg-ktd-black">
                {item.url ? (
                  item.fileType === "photo" ? (
                    <img src={item.url} alt={item.caption || item.fileName} loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <video src={item.url} controls preload="metadata" className="h-full w-full object-cover" />
                  )
                ) : (
                  <div className="media-frame grid h-full place-items-center text-white">
                    {item.fileType === "photo" ? <ImageIcon className="size-10 text-ktd-softGold" /> : <Play className="size-10 text-ktd-softGold" />}
                  </div>
                )}
              </div>
              <div className="border-t border-ktd-gold/15 p-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#8a6c14]">
                  {item.fileType} - {formatDate(item.createdAt)}
                </p>
                {item.caption && <h2 className="mt-3 text-lg font-extrabold">{item.caption}</h2>}
                <p className="mt-2 text-sm text-ktd-muted">Shared by {item.uploaderName || "Guest"}</p>
              </div>
            </article>
          ))}
        </div>

        {!loading && !media.length && (
          <div className="ivory-card mt-8 rounded-lg p-10 text-center">
            <ImageIcon className="mx-auto size-12 text-[#8a6c14]" />
            <h2 className="mt-5 font-display text-3xl font-bold">No memories have been approved yet.</h2>
            <p className="mt-3 text-ktd-muted">
              {eventDetails?.emptyGalleryMessage ||
                "No memories have been approved yet. Photos and videos will appear here once they are uploaded and approved."}
            </p>
          </div>
        )}

        <div className="mt-8 text-center">
          {cursor && (
            <button
              type="button"
              onClick={() => void load(cursor)}
              className="focus-ring event-button rounded-lg px-6 py-3 text-sm font-extrabold"
            >
              Load More
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
